import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Briefcase, Users, Activity, Award, Star, MapPin } from "lucide-react";
import Layout from "@/components/app/Layout";
import { api, fmtErr } from "@/lib/api";
import { StatusChip, JOB_STATUSES } from "@/components/app/JobCard";
import { COUNTRY_BY_CODE } from "@/lib/countries";

const KPIS = [
  ["open_jobs", "Open jobs", Briefcase, "text-indigo-600 bg-indigo-50"],
  ["candidates", "Candidates", Users, "text-slate-700 bg-slate-100"],
  ["in_progress", "In progress", Activity, "text-cyan-700 bg-cyan-50"],
  ["shortlisted", "Completed", Star, "text-amber-700 bg-amber-50"],
  ["hired", "Hired", Award, "text-emerald-700 bg-emerald-50"],
];

function Kpi({ k, label, Icon, cls, value }) {
  return (
    <div className="tp-card p-4" data-testid={`kpi-${k}`}>
      <div className={`w-8 h-8 rounded-lg grid place-items-center mb-3 ${cls}`}><Icon className="w-4 h-4" /></div>
      <div className="font-display text-3xl font-bold" data-testid={`kpi-${k}-value`}>{value ?? 0}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function JobsTable({ jobs }) {
  if (!jobs.length) return <div className="p-8 text-center text-sm text-slate-500" data-testid="client-jobs-empty">No jobs match.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-[color:var(--bd-sub)]">
          <tr><th className="text-left px-4 py-3">Job</th><th className="text-left px-4 py-3">Status</th>
            {["Candidates", "Invited", "In progress", "Completed", "Hired"].map(h => <th key={h} className="text-right px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {jobs.map(j => (
            <tr key={j.id} className="border-b border-[color:var(--bd-sub)] hover:bg-slate-50" data-testid={`client-job-row-${j.id}`}>
              <td className="px-4 py-3">
                <Link to={`/app/jobs/${j.id}`} className="font-medium hover:text-indigo-600" data-testid={`client-job-link-${j.id}`}>{j.title}</Link>
                <div className="text-xs text-slate-500">{[j.account, j.service_line].filter(Boolean).join(" · ") || "—"}</div>
              </td>
              <td className="px-4 py-3"><StatusChip status={j.status} /></td>
              <td className="px-4 py-3 text-right font-mono">{j.candidates}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-500">{j.invited}</td>
              <td className="px-4 py-3 text-right font-mono">{j.in_progress}</td>
              <td className="px-4 py-3 text-right font-mono">{j.shortlisted}</td>
              <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{j.hired}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentHires({ hires }) {
  return (
    <div className="tp-card p-5" data-testid="recent-hires">
      <div className="font-display font-semibold mb-3">Recent hires</div>
      {hires.length === 0 ? <div className="text-sm text-slate-500">No hires yet. Mark a candidate as Hired from their profile.</div> : (
        <ul className="space-y-3">
          {hires.map(h => (
            <li key={h.id} className="flex items-center gap-3" data-testid={`hire-${h.id}`}>
              <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center text-sm font-semibold shrink-0">{h.name?.[0]?.toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <Link to={`/app/candidates/${h.id}`} className="text-sm font-medium hover:text-indigo-600 truncate block">{h.name}</Link>
                <div className="text-xs text-slate-500 truncate">{h.job_title}{h.hired_at ? ` · ${new Date(h.hired_at).toLocaleDateString()}` : ""}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ClientDashboardPage() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  useEffect(() => {
    setData(null); setError(null);
    api.get(`/clients/${clientId}/dashboard`).then(r => setData(r.data)).catch(err => setError(fmtErr(err.response?.data?.detail) || "Could not load this client"));
  }, [clientId]);
  if (error) return (
    <Layout>
      <div className="tp-card p-12 text-center" data-testid="client-dashboard-error">
        <div className="font-display text-lg mb-1">{error}</div>
        <Link to="/app/clients" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">← Back to clients</Link>
      </div>
    </Layout>
  );
  if (!data) return <Layout><div className="text-slate-400">Loading…</div></Layout>;
  const { client, totals, jobs, recent_hires } = data;
  const country = COUNTRY_BY_CODE[client.country_code];
  const shown = status ? jobs.filter(j => (j.status || "open") === status) : jobs;
  return (
    <Layout>
      <div className="mb-6">
        <Link to="/app/clients" className="text-xs text-slate-500 hover:text-slate-800" data-testid="back-to-clients">← Clients</Link>
        <div className="flex items-center gap-3 mt-2">
          <div className="w-11 h-11 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center font-display text-lg font-semibold">{client.name[0]?.toUpperCase()}</div>
          <div>
            <h1 className="font-display text-3xl font-bold" data-testid="client-dashboard-name">{client.name}</h1>
            <div className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{country ? country.name : "Country not set"} · {totals.total_jobs} job{totals.total_jobs === 1 ? "" : "s"} total</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {KPIS.map(([k, label, Icon, cls]) => <Kpi key={k} k={k} label={label} Icon={Icon} cls={cls} value={totals[k]} />)}
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="tp-card overflow-hidden" data-testid="client-jobs-table">
          <div className="flex items-center justify-between gap-3 p-4 border-b border-[color:var(--bd-sub)]">
            <div className="font-display font-semibold">Jobs</div>
            <select data-testid="client-jobs-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm px-3 py-1.5 rounded-lg bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none">
              <option value="">All statuses</option>
              {JOB_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <JobsTable jobs={shown} />
        </div>
        <RecentHires hires={recent_hires} />
      </div>
    </Layout>
  );
}
